import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
}

/*
   Fades and lifts its content into place the first time it scrolls into view.
   The first render is identical on server and client (hidden), and the
   browser-only IntersectionObserver is only touched after mount.
   Visitors who prefer reduced motion always see the content in place.
*/
export const Reveal: FC<RevealProps> = ({ children, className, delay = 0 }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const node = ref.current

        if (!node) return

        if (!('IntersectionObserver' in window)) {
            setVisible(true)
            return
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setVisible(true)
                observer.disconnect()
            }
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })

        observer.observe(node)

        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={ref}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}
            className={cn(
                'transition-[opacity,transform] duration-[900ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] motion-reduce:transition-none',
                visible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100',
                className
            )}
        >
            {children}
        </div>
    )
}
